import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextareaListen from "./Pronunciation/TextareaListen";
import TextareaGenerate from "./Pronunciation/TextareaGenerate";
import TextInstruction from "./Pronunciation/TextInstruction";
import TextareaError from "./Pronunciation/TextareaError";
import RecState from "./Pronunciation/RecState";

const TextArea = ({ text, setText, selectedLanguage }) => {
    const { t } = useTranslation();
    const textareaRef = useRef(null);
    const remainingCharacterCountRef = useRef(null);
    const textareaMaxLength = 404;

    const [remainingCharacters, setRemainingCharacters] = useState(0);
    const [currentlyListening, setCurrentlyListening] = useState(false);
    const [currentlyRecording, setCurrentlyRecording] = useState(false);
    const [textareaHasError, setTextareaHasError] = useState(false);

    const [recordingState, setRecordingState] = useState("idle"); // "idle", "recording", "analyzing", "result"

    useEffect(() => {
        if (text.trim().length > 0) {
            setTextareaHasError(false);
        }
    }, [text]);

    const handleTextChange = (e) => {
        setText(e.target.value);
        const textLength = e.target.value.length;
        if (textLength < 305) {
            remainingCharacterCountRef.current?.classList.add("hidden");
        } else {
            remainingCharacterCountRef.current?.classList.remove("hidden");
            setRemainingCharacters(textareaMaxLength - textLength);
        }
    };

    const handleClear = () => {
        setText("");
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
        remainingCharacterCountRef.current?.classList.add("hidden");
    };

    const autoGrow = (element) => {
        element.style.height = "auto";
        element.style.height = `${element.scrollHeight}px`;
    };

    useEffect(() => {
        if (textareaRef.current) {
            autoGrow(textareaRef.current);
        }
    }, [text]);

    return (
        <div className="p-4">
            <div
                className={`flex flex-col w-full max-w-[620px] mx-auto p-4 border ${
                    textareaHasError ? "border-red-500" : "border-border"
                } rounded-lg bg-secondary`}
            >
                <textarea
                    ref={textareaRef}
                    className="w-full text-xl font-medium text-gray-800 resize-none outline-none bg-secondary min-h-[50px] overflow-hidden"
                    value={text}
                    onChange={handleTextChange}
                    placeholder={t("textAreaPlaceholder")}
                    maxLength={textareaMaxLength}
                />

                <div className="relative flex flex-col items-start space-y-2 sm:mt-3">
                    <TextareaError textareaHasError={textareaHasError} />
                    <TextareaGenerate setText={setText} selectedLanguage={selectedLanguage} />

                    <TextareaListen
                        text={text}
                        selectedLanguage={selectedLanguage}
                        currentlyListening={currentlyListening}
                        setCurrentlyListening={setCurrentlyListening}
                        setTextareaHasError={setTextareaHasError}
                    />

                    <div className="absolute bottom-0 right-0 flex items-center">
                        {/* available characters when close to max */}
                        <p
                            ref={remainingCharacterCountRef}
                            className="text-gray-400 text-xs mr-2 hidden"
                        >
                            {remainingCharacters}
                        </p>

                        {/* Bin icon in the bottom-right corner */}
                        <button
                            className="p-2 border-border rounded-md bg-transparent border border-border hover:bg-gray-200 bg-gray-100"
                            onClick={handleClear}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="black"
                                className="size-6 text-black"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <br></br>
            <TextInstruction
                text={(() => {
                    switch (recordingState) {
                        case "idle":
                            return t("textinstruction.click-to-record");
                        case "recording":
                            return t("textinstruction.click-to-end");
                        default:
                            return "";
                    }
                })()}
            />
            <RecState
                currentlyRecording={currentlyRecording}
                setCurrentlyRecording={setCurrentlyRecording}
                recordingState={recordingState}
                setRecordingState={setRecordingState}
                text={text}
                selectedLanguage={selectedLanguage}
                setTextareaHasError={setTextareaHasError}
            />
            {/* <div className="flex items-center justify-center space-x-4">
                <RecordingButton
                    currentlyRecording={currentlyRecording}
                    setCurrentlyRecording={setCurrentlyRecording}
                    setRecordingState={setRecordingState}
                />
                <div className="absolute -translate-x-[calc(50%+4.5rem)] md:-translate-x-[calc(50%+5rem-1px)] self-start">
                    <AudioVisualizer />
                </div>
            </div> */}
        </div>
    );
};

export default TextArea;
